import { useRef, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import styles from "./styles";
import useCss from "../../hooks/useCss";
import type { ChangeEvent, ReactNode } from "react";

interface Props {
  value?: string | number;
  name?: string | null;
  variant?: "outlined" | "filled" | "standard";
  label?: ReactNode;
  disabled?: boolean | null;
  placeholder?: ReactNode;
  isError?: boolean | null;
  errorMessage?: ReactNode;
  required?: boolean | null;
  handleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  displayCounter?: boolean;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  readOnly?: boolean;
  validateFirstCharacter?: boolean;
  preventAutoFocus?: boolean;
  focused?: boolean;
  error?: boolean;
  className?: string;
  [key: string]: unknown;
}

const SbuxTextField = ({
  value = "",
  name = null,
  variant = "outlined",
  label = null,
  disabled = null,
  placeholder = null,
  isError = null,
  errorMessage = null,
  required = null,
  handleChange,
  displayCounter = false,
  maxLength,
  multiline = false,
  rows = 4,
  readOnly = false,
  validateFirstCharacter = true,
  preventAutoFocus = false,
  ...props
}: Props) => {
  const classes = useCss(styles);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevMultilineRef = useRef(multiline);
  const [firstCharError, setFirstCharError] = useState(false);
  const [firstCharErrorMessage, setFirstCharErrorMessage] = useState("");

  const restrictedFirstChars = ["=", "-", "@"];

  const getFirstCharErrorMessage = () =>
    `First character cannot be one of: (${restrictedFirstChars.join(", ")})`;

  const validateFirstChar = (inputValue: string) => {
    if (!validateFirstCharacter || !inputValue || inputValue.length === 0) {
      setFirstCharError(false);
      setFirstCharErrorMessage("");
      return true;
    }

    const firstChar = inputValue.charAt(0);
    if (restrictedFirstChars.includes(firstChar)) {
      setFirstCharError(true);
      setFirstCharErrorMessage(getFirstCharErrorMessage());
      return false;
    }

    setFirstCharError(false);
    setFirstCharErrorMessage("");
    return true;
  };

  useEffect(() => {
    if (validateFirstCharacter && value) {
      validateFirstChar(value.toString());
    }
  }, [value, validateFirstCharacter]);

  useEffect(() => {
    const multilineChanged = prevMultilineRef.current !== multiline;
    prevMultilineRef.current = multiline;

    if (
      inputRef.current &&
      !readOnly &&
      !preventAutoFocus &&
      value !== undefined &&
      value !== null
    ) {
      const endPosition = value.toString().length;
      const cursorPosition = multilineChanged
        ? endPosition
        : ((
            inputRef.current as HTMLInputElement & {
              selectionStart: number | null;
            }
          ).selectionStart ?? endPosition);

      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, [multiline, value, readOnly, preventAutoFocus]);

  const calcStyles: string[] = [classes.textField];
  if (multiline) {
    calcStyles.push(classes.multiline);
  }
  if (readOnly) {
    calcStyles.push(classes.readOnly);
  }
  const propsClassName = props.className as string | undefined;
  if (propsClassName?.trim().length ?? 0 > 0) {
    calcStyles.push(propsClassName!.trim());
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    const inputValue = e.target.value;

    if (validateFirstCharacter && inputValue.length > 0) {
      const firstChar = inputValue.charAt(0);
      if (restrictedFirstChars.includes(firstChar)) {
        e.preventDefault();
        setFirstCharError(true);
        setFirstCharErrorMessage(getFirstCharErrorMessage());
        return;
      }
    }

    setFirstCharError(false);
    setFirstCharErrorMessage("");

    if (handleChange) {
      handleChange(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (validateFirstCharacter && !readOnly) {
      const cursorPosition = (e.target as HTMLInputElement).selectionStart ?? 0;

      if (cursorPosition === 0 && restrictedFirstChars.includes(e.key)) {
        e.preventDefault();
        setFirstCharError(true);
        setFirstCharErrorMessage(getFirstCharErrorMessage());
        return;
      }
    }
  };

  const hasError = isError || firstCharError;

  const effectiveErrorMessage = firstCharError
    ? firstCharErrorMessage
    : errorMessage;

  // Omit our custom props before spreading to TextField
  const { className: _className, ...restProps } = props;

  return (
    <TextField
      {...restProps}
      inputRef={inputRef}
      label={label}
      variant={variant}
      name={name ?? undefined}
      disabled={disabled ?? undefined}
      value={value || ""}
      className={`${calcStyles.join(" ")}`}
      placeholder={placeholder as string | undefined}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      inputProps={{
        maxLength,
        readOnly,
      }}
      multiline={multiline}
      rows={multiline ? rows : 1}
      helperText={
        <Box component="span" className={classes.holder}>
          {hasError && (
            <Box
              component="span"
              style={{ fontSize: 12, flex: 1, color: "#d32f2f" }}
            >
              {effectiveErrorMessage && <span>{effectiveErrorMessage}</span>}
            </Box>
          )}
          {!readOnly && displayCounter && (
            <span className={classes.counter}>
              {`${(value || "").toString().length}/${maxLength}`}
            </span>
          )}
        </Box>
      }
      error={hasError ?? undefined}
      required={required ?? undefined}
    />
  );
};

export default SbuxTextField;
